<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready" />
        <b-form @submit.prevent="onSubmit">
          <b-card header="Workforce Member Tasks" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <hm-alert />
            <!-- Active workforce member -->
            <div v-if="employee.active">
              <b-form-group label="What is the workforce member's name?" label-class="font-weight-bold">
                <b-form-input
                  name="fullName"
                  type="text"
                  class="form-control"
                  placeholder="Full Name"
                  autocomplete="name"
                  v-model="employee.name"
                  v-validate="'required'"
                  :disabled="busy"
                  :class="{ 'is-invalid': errors.has('fullName') && submitted }"
                ></b-form-input>
              </b-form-group>
              <b-form-group label="What role / job duty is the workforce member assigned?" label-class="font-weight-bold">
                <b-form-select
                  name="selectRole"
                  class="form-control"
                  :options="roleMap.Roles"
                  v-model="employee.role"
                  v-validate="'required'"
                  :disabled="busy"
                  :class="{ 'is-invalid': errors.has('selectRole') && submitted }"
                >
                  <template slot="first">
                    <option :value="null" disabled>-- Please select an option --</option>
                  </template>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Authorized ePHI access" label-class="font-weight-bold">
                <hm-roleslist :role="role" :item="employee.role" />
              </b-form-group>
              <b-form-group
                label="Is use of a mobile device (laptop, tablet, smart phone) necessary and appropriate for the workforce member to perform his or her job responsibilities?"
                label-class="font-weight-bold"
              >
                <hm-switch class="mx-1" v-model="employee.electronicAccess" data-type="yesno" :disabled="busy" />
              </b-form-group>
              <b-form-group
                label="Is remote access to information systems necessary and appropriate for the workforce member to perform his or her job responsibilities?"
                label-class="font-weight-bold"
              >
                <hm-switch class="mx-1" v-model="employee.remoteAccess" data-type="yesno" :disabled="busy" />
              </b-form-group>
              <b-form-group label="Are any keys, combinations, access cards, etc. assigned to the workforce member?" label-class="font-weight-bold">
                <b-form-input
                  name="txtInventory"
                  type="text"
                  class="form-control"
                  placeholder="Front door key, billing office key, swipe card, server room combo, etc."
                  v-model="employee.inventory"
                  :disabled="busy"
                ></b-form-input>
              </b-form-group>
              <b-form-group label="Which agreements has the workforce member signed?" label-class="font-weight-bold">
                <div v-for="(value, key) in workforceMap.Agreements" :key="key">
                  <hm-switch class="mx-1 p-1" size="sm" :value="key" :label="value" v-model="employee.agreements" data-type="check" :disabled="busy" />
                </div>
              </b-form-group>
              <b-form-group label="Most Recent Training" label-class="font-weight-bold">
                <p>
                  Training can be added within the
                  <a ui-sref="app.training-docs">Training</a> page.
                </p>
                <hm-traininglist :show-date="true" :training="employee.training" />
              </b-form-group>
            </div>

            <!-- Inactive workforce member -->
            <div v-else>
              <b-form-group>
                <div slot="label">
                  <strong>Name:</strong>
                  {{employee.name}}
                </div>
              </b-form-group>
              <b-form-group label="Employment termination for this person was:" label-class="font-weight-bold">
                <hm-switch class="mx-1" size="sm" color="success" label="Amicable" v-model="employee.separation.amicable" data-type="check" :disabled="busy" />
                <hm-switch class="mx-1 pt-2" size="sm" color="danger" label="Hostile" v-model="hostile" data-type="check" :disabled="busy" />
              </b-form-group>
              <b-form-group label="Have you collected the following physical access items assigned to this person?" label-class="font-weight-bold">
                <p class="mx-4">
                  <span class="text-danger" v-if="employee.inventory && employee.inventory.length > 0">{{employee.inventory}}</span>
                  <span class="text-danger" v-else>No items assigned</span>
                </p>
                <hm-switch class="mx-1" v-model="employee.separation.inventory" data-type="yesno" :disabled="busy" />
                <hm-note v-if="employee.inventory && employee.inventory.length > 0 && !employee.separation.inventory">
                  <u>Note</u>: Consider rekeying doors if you are unable to collect keys, combinations, access cards, etc.. Record the modification within the Maintenance Logbook (found under the Practice menu item).
                </hm-note>
              </b-form-group>
              <b-form-group>
                <div slot="label">
                  <strong>Have you collected all ePHI inventory items assigned to this person?</strong>
                  <b-button variant="link" to="/inventory-list">See inventory list</b-button>
                </div>
                <hm-switch class="mx-1" v-model="employee.separation.ePHI" data-type="yesno" :disabled="busy" />
              </b-form-group>
              <b-form-group label="Have you disabled all user account credentials assigned to this person, e.g., log in usernames and passwords?" label-class="font-weight-bold">
                <hm-switch class="mx-1" v-model="employee.separation.credentials" data-type="yesno" :disabled="busy" />
              </b-form-group>
            </div>

            <div slot="footer">
              <b-row>
                <b-col cols="6">
                  <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/workforce-list">Back</b-button>
                  <b-button v-if="id && employee.active" type="button" variant="danger" class="px-4 ml-2" :disabled="busy" @click.prevent="dlgDelete = true">Remove</b-button>
                </b-col>
                <b-col cols="6" class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>

          <!-- Inactive workforce member -->
          <b-card header="Other Information" v-if="!employee.active && ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner />
              </slot>
            </Loading>
            <span>
              Job role:
              <strong>{{roleName}}</strong>
            </span>
            <br />
            <span>
              Use of a mobile device necessary/appropriate:
              <strong>{{employee.electronicAccess ? 'Yes' : 'No'}}</strong>
            </span>
            <br />
            <span>
              Remote access to information systems necessary/appropriate:
              <strong>{{employee.remoteAccess ? 'Yes' : 'No'}}</strong>
            </span>
            <div v-for="(value, key) in workforceMap.Agreements" :key="key">
              {{value}} signed:
              <strong>{{hasSignedAgreement(key) ? 'Yes' : 'No'}}</strong>
            </div>
            <br />
            <div>
              <u>Most recent training</u>
              <hm-traininglist :show-date="true" :show-icon="false" :training="employee.training" />
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>

    <!-- Delete Confirmation Modal Dialog -->
    <b-modal id="dlgDelete" class="modal-primary" hide-header-close v-model="dlgDelete">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-trash pr-2"></i>Remove Workforce Member
      </div>
      <p>Are you sure you want to remove this workforce member?</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgDelete = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgDelete = false; onDelete()">Yes</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { trainingService, workforceService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'WorkforceEdit',
  props: ['id'],
  data() {
    return {
      employee: {
        id: '',
        active: true,
        name: '',
        role: null,
        electronicAccess: false,
        remoteAccess: false,
        agreements: [],
        training: {},
        separation: {
          amicable: true,
          inventory: false,
          ePHI: false,
          credentials: false
        }
      },
      role: {
        writtenDesc: false,
        segregation: false,
        globalAccess: true,
        roles: {
        }
      },
      training: {},
      dlgDelete: false,
      ready: false,
      busy: false,
      submitted: false
    }
  },
  computed: {
    ...mapState('maps', ['roleMap', 'trainingMap', 'workforceMap']),
    hostile: {
      get: function () {
        return !this.employee.separation.amicable;
      },
      set: function (newValue) {
        this.employee.separation.amicable = !this.employee.separation.amicable;
      }
    },
    roleName() {
      return this.roleMap.Roles[this.employee.role];
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    hasSignedAgreement(key) {
      return this.employee.agreements.find(a => a === key);
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return workforceService.setWorkforce(this.employee)
              .then(data => {
                this.setProgress('WorkforceSet');
                this.$router.push('/workforce-list');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving employee.' });
              });
          } else {
            this.alertError('Please complete employee information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onDelete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      return workforceService.deleteWorkforce(this.employee)
        .then(data => {
          if (this.employee.active) {
            this.employee.active = false;
          } else {
            this.$router.push('/workforce-list');
          }
        })
        .catch(err => {
          this.alertException({ err, message: 'Error deleting employee.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchRoleMap'),
        this.$store.dispatch('maps/fetchTrainingMap'),
        this.$store.dispatch('maps/fetchWorkforceMap'),
        workforceService.getRole(),
        trainingService.getTrainingCache()
      ]).then((values) => {
        this.role = values[3];
        this.training = values[4];

        let emptyTraining = Enumerable.from(this.trainingMap.Training)
          .toObject(function (k) { return k.key; }, function (v) { return ''; });

        if (this.id) {
          return workforceService.getWorkforce(this.id)
            .then(data => {
              if (!data.agreements) data.agreements = [];
              if (!data.separation) data.separation = { amicable: true, inventory: false, ePHI: false, credentials: false };

              // join workforce list to training
              data.training = Enumerable.from(this.training.training)
                .where(function (x) { return x.id == data.id; })
                .firstOrDefault(null, { training: emptyTraining }).training;

              this.employee = data;
            })
            .catch(err => {
              this.alertException({ err, message: 'Error loading workforce.' });
            });
        } else {
          this.employee.training = emptyTraining;
        }
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading data.' });
      }).finally(() => {
        this.ready = true;
      })
    }

  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>